import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { TenantService } from "@/services/tenantService";
import type { CreatePlanData } from "@/types/tenant";
import toast from "react-hot-toast";
import {
  TbToggleLeft,
  TbToggleRight,
  TbCheck,
  TbX,
  TbLoader,
} from "react-icons/tb";

const FEATURE_FLAGS = [
  { key: "feature_arcgis", label: "ArcGIS Integration" },
  { key: "feature_superset", label: "Superset Analytics" },
  { key: "feature_realtime", label: "Real-time Updates" },
  { key: "feature_bulk_assignments", label: "Bulk Assignments" },
  { key: "feature_reporting", label: "Reporting" },
  { key: "feature_asset_management", label: "Asset Management" },
];

export const PlatformPlanForm: React.FC = () => {
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const isEditing = !!planId;

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEditing);
  const [formData, setFormData] = useState<CreatePlanData>({
    name: "",
    slug: "",
    description: "",
    max_users: 10,
    max_tasks: 1000,
    feature_superset: false,
    feature_arcgis: false,
    feature_realtime: false,
    feature_bulk_assignments: false,
    feature_reporting: false,
    feature_asset_management: false,
    price_monthly: 0,
    is_active: true,
    sort_order: 0,
  });
  const [slugStatus, setSlugStatus] = useState<{
    checking: boolean;
    available: boolean | null;
  }>({ checking: false, available: null });
  const [originalSlug, setOriginalSlug] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  // Load existing plan for edit mode
  useEffect(() => {
    if (!planId) return;

    const loadPlan = async () => {
      setLoading(true);
      try {
        const plans = await TenantService.listPlans();
        const plan = plans.find((p) => p.id === planId);
        if (plan) {
          setFormData({
            name: plan.name,
            slug: plan.slug,
            description: plan.description,
            max_users: plan.max_users,
            max_tasks: plan.max_tasks,
            feature_superset: plan.feature_superset,
            feature_arcgis: plan.feature_arcgis,
            feature_realtime: plan.feature_realtime,
            feature_bulk_assignments: plan.feature_bulk_assignments,
            feature_reporting: plan.feature_reporting,
            feature_asset_management: plan.feature_asset_management,
            price_monthly: plan.price_monthly,
            is_active: plan.is_active,
            sort_order: plan.sort_order,
          });
          setOriginalSlug(plan.slug);
        } else {
          toast.error("Plan not found");
          navigate("/platform/plans");
        }
      } catch {
        toast.error("Failed to load plan");
      } finally {
        setLoading(false);
      }
    };
    loadPlan();
  }, [planId, navigate]);

  // Debounced slug uniqueness check
  useEffect(() => {
    const slug = formData.slug;
    if (!slug || slug.length < 2 || slug === originalSlug) {
      setSlugStatus({ checking: false, available: null });
      return;
    }

    setSlugStatus({ checking: true, available: null });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const plans = await TenantService.listPlans();
        const taken = plans.some(
          (p) => p.slug === slug && p.id !== planId
        );
        setSlugStatus({ checking: false, available: !taken });
      } catch {
        setSlugStatus({ checking: false, available: null });
      }
    }, 400);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [formData.slug, originalSlug, planId]);

  const handleChange = (field: string, value: string | number | boolean | null) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.slug.trim()) {
      toast.error("Name and slug are required");
      return;
    }

    if (slugStatus.available === false) {
      toast.error("Slug is already taken");
      return;
    }

    setSaving(true);
    try {
      if (isEditing) {
        await TenantService.updatePlan(planId, formData);
        toast.success("Plan updated");
      } else {
        await TenantService.createPlan(formData);
        toast.success("Plan created");
      }
      navigate("/platform/plans");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to save plan"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-4 border-[#374795] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      <h2 className="text-lg font-semibold text-[#444]">
        {isEditing ? "Edit Plan" : "Create New Plan"}
      </h2>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 space-y-5">
        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-[#444] mb-1.5">
            Plan Name
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => {
              const name = e.target.value;
              handleChange("name", name);
              if (!isEditing && !originalSlug) {
                handleChange(
                  "slug",
                  name
                    .toLowerCase()
                    .replace(/[^a-z0-9]/g, "-")
                    .replace(/-+/g, "-")
                );
              }
            }}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#374795] focus:border-transparent"
            placeholder="Professional"
          />
        </div>

        {/* Slug */}
        <div>
          <label className="block text-sm font-medium text-[#444] mb-1.5">
            Slug
          </label>
          <div className="relative">
            <input
              type="text"
              value={formData.slug}
              onChange={(e) =>
                handleChange(
                  "slug",
                  e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "")
                )
              }
              className={`w-full px-4 py-2.5 pr-10 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent ${
                slugStatus.available === true
                  ? "border-green-400 focus:ring-green-500"
                  : slugStatus.available === false
                    ? "border-red-400 focus:ring-red-500"
                    : "border-gray-300 focus:ring-[#374795]"
              }`}
              placeholder="professional"
            />
            {formData.slug.length >= 2 && formData.slug !== originalSlug && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {slugStatus.checking ? (
                  <TbLoader className="w-4 h-4 animate-spin text-gray-400" />
                ) : slugStatus.available === true ? (
                  <TbCheck className="w-4 h-4 text-green-500" />
                ) : slugStatus.available === false ? (
                  <TbX className="w-4 h-4 text-red-500" />
                ) : null}
              </div>
            )}
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-[#444] mb-1.5">
            Description
          </label>
          <textarea
            value={formData.description || ""}
            onChange={(e) => handleChange("description", e.target.value)}
            rows={2}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#374795] focus:border-transparent resize-none"
            placeholder="A brief description of this plan"
          />
        </div>

        {/* Limits */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#444] mb-1.5">
              Max Users
            </label>
            <input
              type="number"
              value={formData.max_users}
              onChange={(e) =>
                handleChange("max_users", parseInt(e.target.value) || 0)
              }
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#374795] focus:border-transparent"
            />
            <p className="text-xs text-[#828282] mt-1">0 = unlimited</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#444] mb-1.5">
              Max Tasks
            </label>
            <input
              type="number"
              value={formData.max_tasks}
              onChange={(e) =>
                handleChange("max_tasks", parseInt(e.target.value) || 0)
              }
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#374795] focus:border-transparent"
            />
            <p className="text-xs text-[#828282] mt-1">0 = unlimited</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#444] mb-1.5">
              Price ($/mo)
            </label>
            <input
              type="number"
              value={formData.price_monthly}
              onChange={(e) =>
                handleChange(
                  "price_monthly",
                  parseFloat(e.target.value) || 0
                )
              }
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#374795] focus:border-transparent"
            />
          </div>
        </div>

        {/* Feature flags */}
        <div className="space-y-2">
          <p className="text-sm font-medium text-[#444]">Feature Flags</p>
          {FEATURE_FLAGS.map(({ key, label }) => (
            <div
              key={key}
              className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg"
            >
              <span className="text-sm text-[#444]">{label}</span>
              <button
                onClick={() =>
                  handleChange(
                    key,
                    !formData[key as keyof typeof formData]
                  )
                }
                className="text-[#828282] hover:text-[#444] transition-colors cursor-pointer"
              >
                {formData[key as keyof typeof formData] ? (
                  <TbToggleRight className="w-7 h-7 text-green-500" />
                ) : (
                  <TbToggleLeft className="w-7 h-7 text-gray-400" />
                )}
              </button>
            </div>
          ))}
        </div>

        {/* Sort order & active */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#444] mb-1.5">
              Sort Order
            </label>
            <input
              type="number"
              value={formData.sort_order}
              onChange={(e) =>
                handleChange("sort_order", parseInt(e.target.value) || 0)
              }
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#374795] focus:border-transparent"
            />
          </div>
          <div className="flex items-end pb-1">
            <label className="flex items-center gap-3 cursor-pointer">
              <button
                onClick={() => handleChange("is_active", !formData.is_active)}
                className="cursor-pointer"
              >
                {formData.is_active ? (
                  <TbToggleRight className="w-7 h-7 text-green-500" />
                ) : (
                  <TbToggleLeft className="w-7 h-7 text-gray-400" />
                )}
              </button>
              <span className="text-sm font-medium text-[#444]">Active</span>
            </label>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2">
          <button
            onClick={() => navigate("/platform/plans")}
            className="px-5 py-2.5 text-sm font-medium text-[#444] bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || slugStatus.available === false || slugStatus.checking}
            className="px-5 py-2.5 text-sm font-medium text-white bg-[#374795] rounded-lg hover:bg-[#1c398e] disabled:opacity-50 transition-colors cursor-pointer"
          >
            {saving
              ? "Saving..."
              : isEditing
                ? "Save Changes"
                : "Create Plan"}
          </button>
        </div>
      </div>
    </div>
  );
};
